import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { ManualIssuePanel } from '../components/ManualIssuePanel';
import { QrCode, Package, Users, CheckCircle2, AlertCircle, Download, Plus, Search, Bell, PhoneCall, Loader2, FileSpreadsheet, FileText, Archive } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Dark Yellow + Black theme tokens ───────────────────────────────────────
const T = {
  card: 'bg-neutral-900 border border-yellow-500/25 rounded-2xl shadow-[0_0_0_1px_rgba(250,204,21,0.05)]',
  cardHover: 'hover:border-yellow-400/60 transition-colors',
  input: 'bg-neutral-950 border border-yellow-500/30 text-yellow-100 placeholder-yellow-500/40 focus:outline-none focus:ring-2 focus:ring-yellow-400/50',
  btnPrimary: 'inline-flex items-center gap-2 bg-yellow-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-yellow-300 transition-colors',
  btnOutline: 'inline-flex items-center gap-2 border border-yellow-400/60 text-yellow-300 px-4 py-2 rounded-full hover:bg-yellow-400 hover:text-black transition-colors',
  accent: 'text-yellow-300',
  softText: 'text-yellow-200/70',
  mutedText: 'text-yellow-100/50',
  tableHead: 'bg-black text-yellow-300',
  divider: 'border-yellow-500/15',
};

export default function AdminQRPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [tags, setTags] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [qFilter, setQFilter] = useState({ status: 'all', q: '' });
  const [genQty, setGenQty] = useState(100);
  const [genResult, setGenResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allocateFor, setAllocateFor] = useState(null); // tag_id to allocate
  const [orderIdInput, setOrderIdInput] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const loadOverview = async () => { try { const r = await axios.get(`${API}/admin/qr/overview`, { headers }); setOverview(r.data); } catch (e) { console.error(e); } };
  const loadTags = async () => { try { const p = new URLSearchParams(Object.entries(qFilter).filter(([, v]) => v && v !== 'all')); const r = await axios.get(`${API}/admin/qr/tags?${p.toString()}`, { headers }); setTags(r.data); } catch (e) {} };
  const loadReqs = async () => { try { const r = await axios.get(`${API}/admin/qr/activation-requests`, { headers }); setReqs(r.data); } catch (e) {} };

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    if (tab === 'inventory') loadTags();
    if (tab === 'requests') loadReqs();
    if (tab === 'overview') loadOverview();
    // eslint-disable-next-line
  }, [tab]);

  const generate = async () => {
    if (genQty < 1 || genQty > 1000) { toast({ title: 'Quantity must be 1–1000' }); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/qr/generate`, { quantity: genQty }, { headers });
      setGenResult(data);
      toast({ title: `Generated ${data.generated} tags` });
      loadOverview();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); } finally { setBusy(false); }
  };

  const download = async (path, filename) => {
    try {
      const r = await axios.get(`${API}${path}`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) { toast({ title: 'Download failed', description: e.response?.data?.detail || '' }); }
  };

  const downloadCodesCsv = () => {
    if (!genResult) return;
    const rows = ['Tag ID,Permanent URL,Activation Code'];
    genResult.codes.forEach((c) => rows.push(`${c.tag_id},${c.permanent_url},${c.activation_code}`));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `activation-codes-${genResult.from}-${genResult.to}.csv`; a.click();
  };

  const deleteTag = async (tag_id) => {
    if (!window.confirm(`Delete ${tag_id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/qr/tag/${tag_id}`, { headers });
      toast({ title: 'Tag deleted' });
      loadTags(); loadOverview();
    } catch (e) { toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again' }); }
  };

  const allocate = async () => {
    if (!allocateFor || !orderIdInput.trim()) return;
    try {
      await axios.post(`${API}/admin/qr/allocate`, { tag_id: allocateFor, order_id: orderIdInput.trim() }, { headers });
      toast({ title: `Allocated ${allocateFor} → ${orderIdInput}` });
      setAllocateFor(null); setOrderIdInput('');
      loadTags(); loadOverview();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
  };

  const updateReq = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/qr/activation-requests/${id}`, { status }, { headers });
      toast({ title: `Marked ${status}` });
      loadReqs();
    } catch { toast({ title: 'Failed' }); }
  };

  return (
    <Layout>
      <div className="bg-black min-h-screen" data-testid="admin-qr-page">
        {/* Header */}
        <div className="relative overflow-hidden border-b border-yellow-500/20" style={{background: 'linear-gradient(135deg, #000000 0%, #1a1400 55%, #000000 100%)'}}>
          <div className="absolute -left-24 -top-24 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl"></div>
          <div className="absolute -right-24 -bottom-24 w-[28rem] h-[28rem] rounded-full bg-yellow-500/10 blur-3xl"></div>
          <div className="max-w-7xl mx-auto px-4 py-10 relative">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 text-yellow-300 rounded-full px-3 py-1 text-xs mb-3 backdrop-blur-sm"><QrCode size={12} /> Emergency QR Tags · Operational Command</div>
                <h1 className="h-display text-4xl md:text-5xl text-yellow-100">Tag <em className="font-serif-display italic font-normal text-yellow-400">Management</em></h1>
                <p className={`text-sm mt-2 max-w-xl ${T.softText}`}>Generate, allocate, activate &amp; audit — everything the printing vendor and the customer never see.</p>
              </div>
              <Link to="/admin" className="text-sm text-yellow-300 hover:text-yellow-100 transition-colors" data-testid="back-to-admin">← Back to Admin</Link>
            </div>
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 py-8">
          {/* Tabs */}
          <div className={`flex gap-1 overflow-x-auto mb-6 bg-neutral-900 rounded-2xl px-2 py-2 border border-yellow-500/20`}>
            {[['overview', 'Overview'], ['generate', 'Generate'], ['issue', 'Manual Issue'], ['inventory', 'Inventory'], ['requests', 'Activation Requests'], ['exports', 'Exports']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold rounded-full whitespace-nowrap transition-colors ${tab === k ? 'bg-yellow-400 text-black' : 'text-yellow-200/70 hover:text-yellow-100 hover:bg-yellow-400/10'}`} data-testid={`qr-tab-${k}`}>{l}</button>
            ))}
          </div>

          {tab === 'overview' && overview && (
            <div className="space-y-5" data-testid="qr-overview">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat icon={QrCode} label="Total tags" value={overview.total} />
                <Stat icon={Package} label="Available" value={overview.counts.available} />
                <Stat icon={Users} label="Allocated" value={overview.counts.allocated} />
                <Stat icon={CheckCircle2} label="Active" value={overview.counts.active} />
                <Stat icon={AlertCircle} label="Deactivated" value={overview.counts.deactivated} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat icon={Bell} label="Total notifications" value={overview.total_notifications} />
                <Stat icon={PhoneCall} label="Call actions" value={overview.total_calls} />
                <Stat icon={QrCode} label="Total scans" value={overview.total_scans} />
                <Stat icon={AlertCircle} label="Pending req." value={overview.pending_activation_requests} />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className={`${T.card} p-4`}><h4 className={`text-sm font-semibold mb-2 ${T.accent}`}>Recent tags</h4>{overview.recent_tags.map((t) => <p key={t.tag_id} className="text-xs py-1 font-mono text-yellow-100">{t.tag_id} · <span className={`uppercase ${T.mutedText}`}>{t.status}</span></p>)}</div>
                <div className={`${T.card} p-4`}><h4 className={`text-sm font-semibold mb-2 ${T.accent}`}>Recent activations</h4>{overview.recent_activations.map((a) => <p key={a.tag_id + a.activated_at} className="text-xs py-1 text-yellow-100"><span className="font-mono">{a.tag_id}</span> · {new Date(a.activated_at).toLocaleString()}</p>)}{overview.recent_activations.length === 0 && <p className={`text-xs ${T.mutedText}`}>—</p>}</div>
                <div className={`${T.card} p-4`}><h4 className={`text-sm font-semibold mb-2 ${T.accent}`}>Recent scans</h4>{overview.recent_scans.map((s, i) => <p key={i} className="text-xs py-1 text-yellow-100"><span className="font-mono">{s.tag_id}</span> · <span className={`uppercase ${T.mutedText}`}>{s.action}</span> · {new Date(s.created_at).toLocaleTimeString()}</p>)}{overview.recent_scans.length === 0 && <p className={`text-xs ${T.mutedText}`}>—</p>}</div>
              </div>
            </div>
          )}

          {tab === 'generate' && (
            <div className="space-y-4" data-testid="qr-generate">
              <div className={`${T.card} p-6`}>
                <h3 className="font-semibold mb-1 text-yellow-100">Generate a fresh QR tag batch</h3>
                <p className={`text-xs mb-4 ${T.softText}`}>Each tag gets a unique Tag ID, permanent URL, and a one-time-use activation code. Codes are shown only once — download the CSV immediately.</p>
                <div className="flex items-end gap-3 flex-wrap">
                  <label className="block text-xs">
                    <span className={`uppercase ${T.mutedText}`}>Quantity</span>
                    <input type="number" min={1} max={1000} value={genQty} onChange={(e) => setGenQty(parseInt(e.target.value) || 0)} className={`mt-1 block w-32 px-3 py-2 rounded-lg ${T.input}`} data-testid="gen-qty-input" />
                  </label>
                  <button onClick={generate} disabled={busy} className={`${T.btnPrimary} text-sm disabled:opacity-60`} data-testid="generate-btn">{busy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Plus size={14} /> Generate</>}</button>
                </div>
              </div>
              {genResult && (
                <div className={`${T.card} p-6`} data-testid="gen-result">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <div>
                      <h4 className="font-semibold text-yellow-100">✅ Generated {genResult.generated} tags · <span className="font-mono">{genResult.from} → {genResult.to}</span></h4>
                      <p className="text-xs text-red-400 mt-1">⚠ Activation codes shown ONLY once — download the CSV before leaving this page.</p>
                    </div>
                    <button onClick={downloadCodesCsv} className={`${T.btnPrimary} text-sm`} data-testid="download-codes-csv"><Download size={14} /> Download codes CSV</button>
                  </div>
                  <div className={`max-h-96 overflow-auto border ${T.divider} rounded-lg`}>
                    <table className="w-full text-sm">
                      <thead className={`${T.tableHead} sticky top-0`}><tr><th className="text-left py-2 px-3">Tag ID</th><th className="text-left py-2 px-3">URL</th><th className="text-left py-2 px-3">Activation Code</th></tr></thead>
                      <tbody>
                        {genResult.codes.map((c) => (
                          <tr key={c.tag_id} className={`border-t ${T.divider}`}>
                            <td className="py-2 px-3 font-mono text-yellow-100">{c.tag_id}</td>
                            <td className={`py-2 px-3 text-xs truncate max-w-xs ${T.softText}`}>{c.permanent_url}</td>
                            <td className="py-2 px-3 font-mono font-bold text-yellow-300">{c.activation_code}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'inventory' && (
            <div className="space-y-4" data-testid="qr-inventory">
              <div className={`${T.card} p-4 flex items-end gap-3 flex-wrap`}>
                <label className="block text-xs">
                  <span className={`uppercase ${T.mutedText}`}>Status</span>
                  <select value={qFilter.status} onChange={(e) => setQFilter({ ...qFilter, status: e.target.value })} className={`mt-1 block px-3 py-2 rounded-lg text-sm ${T.input}`}>
                    <option value="all">All</option><option value="available">Available</option><option value="allocated">Allocated</option><option value="active">Active</option><option value="deactivated">Deactivated</option>
                  </select>
                </label>
                <label className="block text-xs flex-1 max-w-xs">
                  <span className={`uppercase ${T.mutedText}`}>Search (tag/order/customer)</span>
                  <input value={qFilter.q} onChange={(e) => setQFilter({ ...qFilter, q: e.target.value })} className={`mt-1 w-full px-3 py-2 rounded-lg text-sm ${T.input}`} placeholder="TAG0001, order#, name…" />
                </label>
                <button onClick={loadTags} className={`${T.btnPrimary} text-sm`} data-testid="filter-apply"><Search size={14} /> Apply</button>
              </div>
              <div className={`${T.card} overflow-x-auto`}>
                <table className="w-full text-sm min-w-[900px]">
                  <thead className={`${T.tableHead}`}><tr><th className="text-left py-3 px-3">Tag ID</th><th className="text-left py-3 px-3">URL</th><th className="text-left py-3 px-3">Status</th><th className="text-left py-3 px-3">Order</th><th className="text-left py-3 px-3">Vehicle</th><th className="text-left py-3 px-3">Created</th><th className="text-left py-3 px-3">Activated</th><th className="text-right py-3 px-3">Action</th></tr></thead>
                  <tbody data-testid="tags-table">
                    {tags.map((t) => (
                      <tr key={t.tag_id} className={`border-t ${T.divider}`}>
                        <td className="py-2 px-3 font-mono font-semibold text-yellow-100">{t.tag_id}</td>
                        <td className="py-2 px-3"><a href={`/tag/${t.tag_id.replace('TAG', '')}`} target="_blank" rel="noreferrer" className="text-xs text-yellow-300 underline truncate max-w-xs inline-block">{t.permanent_url}</a></td>
                        <td className="py-2 px-3"><span className={`text-[10px] px-2 py-1 rounded-full font-semibold uppercase ${statusColor(t.status)}`}>{t.status}</span></td>
                        <td className={`py-2 px-3 text-xs ${T.softText}`}>{t.order_number_cache || t.order_id || '—'}</td>
                        <td className="py-2 px-3 text-xs font-mono text-yellow-100">{t.registration_number_cache || '—'}</td>
                        <td className={`py-2 px-3 text-xs ${T.mutedText}`}>{new Date(t.created_at).toLocaleDateString()}</td>
                        <td className={`py-2 px-3 text-xs ${T.mutedText}`}>{t.activated_at ? new Date(t.activated_at).toLocaleString() : '—'}</td>
                        <td className="py-2 px-3 text-right">
                          {t.status === 'available' && <button onClick={() => setAllocateFor(t.tag_id)} className="text-xs text-yellow-300 font-semibold hover:underline mr-3" data-testid={`allocate-${t.tag_id}`}>Allocate</button>}
                          {t.status === 'active' && <span className="text-xs text-green-400 mr-3">✓ Live</span>}
                          {t.status !== 'active' && <button onClick={() => deleteTag(t.tag_id)} className="text-xs text-red-400 hover:underline" data-testid={`delete-${t.tag_id}`}>Delete</button>}
                        </td>
                      </tr>
                    ))}
                    {tags.length === 0 && <tr><td colSpan={8} className={`py-8 text-center text-xs ${T.mutedText}`}>No tags found.</td></tr>}
                  </tbody>
                </table>
              </div>
              {allocateFor && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAllocateFor(null)}>
                  <div className={`${T.card} p-5 max-w-md w-full`} onClick={(e) => e.stopPropagation()} data-testid="allocate-modal">
                    <h3 className="font-semibold mb-2 text-yellow-100">Allocate {allocateFor} to an order</h3>
                    <p className={`text-xs mb-3 ${T.softText}`}>Enter the Order ID or Order # from the existing selling system.</p>
                    <input value={orderIdInput} onChange={(e) => setOrderIdInput(e.target.value)} placeholder="e.g. CNM260901ABCD" className={`w-full px-3 py-2 rounded-lg text-sm mb-3 ${T.input}`} data-testid="allocate-order-input" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setAllocateFor(null)} className={`${T.btnOutline} text-sm`}>Cancel</button>
                      <button onClick={allocate} className={`${T.btnPrimary} text-sm`} data-testid="allocate-confirm">Allocate</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'requests' && (
            <div className="space-y-3" data-testid="qr-requests">
              {reqs.length === 0 && <div className={`${T.card} p-10 text-center text-sm ${T.mutedText}`}>No activation requests yet.</div>}
              {reqs.map((r) => (
                <div key={r.id} className={`${T.card} p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-3`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-yellow-100">{r.customer_name}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-400/15 text-yellow-300 font-semibold uppercase font-mono border border-yellow-400/30">{r.tag_id}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${r.status === 'approved' ? 'bg-green-500/20 text-green-300' : r.status === 'rejected' ? 'bg-red-500/20 text-red-300' : 'bg-yellow-400/20 text-yellow-200'}`}>{r.status}</span>
                    </div>
                    <p className={`text-xs mt-1 ${T.softText}`}>Order: {r.order_number || r.order_id} · Phone: {r.customer_phone}</p>
                    <p className="text-sm text-yellow-100 mt-2"><span className="font-semibold text-yellow-300">Reason:</span> {r.reason}</p>
                    {r.message && <p className={`text-xs italic mt-1 ${T.softText}`}>"{r.message}"</p>}
                    <p className={`text-[10px] mt-2 ${T.mutedText}`}>Requested {new Date(r.requested_at).toLocaleString()}</p>
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => updateReq(r.id, 'approved')} className="text-xs bg-yellow-400 text-black font-semibold px-3 py-1.5 rounded-full hover:bg-yellow-300">Approve</button>
                      <button onClick={() => updateReq(r.id, 'rejected')} className="text-xs border border-red-400/60 text-red-300 px-3 py-1.5 rounded-full hover:bg-red-500/10">Reject</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'issue' && <ManualIssuePanel T={T} headers={headers} onDone={loadOverview} />}

          {tab === 'exports' && (
            <div className="grid md:grid-cols-2 gap-4" data-testid="qr-exports">
              <ExportCard icon={FileSpreadsheet} title="Tag Inventory XLSX" desc="Full inventory with statuses, orders, customers, vehicles." action={() => download('/admin/qr/export/inventory.xlsx', 'qr-inventory.xlsx')} />
              <ExportCard icon={FileSpreadsheet} title="Activation Codes XLSX" desc="Metadata only — codes are stored encrypted and shown to each customer in My Orders." action={() => download('/admin/qr/export/activation-codes.xlsx', 'activation-codes.xlsx')} />
              <ExportCard icon={FileText} title="QR Sheet PDF" desc="Print-ready 3×5 grid of QR codes per A4 page." action={() => download('/admin/qr/export/qr-sheet.pdf', 'qr-sheet.pdf')} />
              <ExportCard icon={Archive} title="QR Images ZIP" desc="Individual PNG files named TAGnnnn.png for printing vendors." action={() => download('/admin/qr/export/qr-images.zip', 'qr-images.zip')} />
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className={`${T.card} ${T.cardHover} p-4 relative overflow-hidden group`}>
    <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full bg-yellow-400/5 group-hover:bg-yellow-400/15 transition-colors"></div>
    <Icon size={16} className="text-yellow-300 mb-1 relative" />
    <p className="h-display text-2xl relative text-yellow-100">{value}</p>
    <p className={`text-xs relative ${T.softText}`}>{label}</p>
  </div>
);

const ExportCard = ({ icon: Icon, title, desc, action }) => (
  <div className={`${T.card} p-5`}>
    <Icon size={18} className="text-yellow-300 mb-2" />
    <h4 className="font-semibold text-sm text-yellow-100">{title}</h4>
    <p className={`text-xs mt-1 mb-3 ${T.softText}`}>{desc}</p>
    <button onClick={action} className={`${T.btnPrimary} text-sm`} data-testid={`export-${title.split(' ')[0].toLowerCase()}`}><Download size={14} /> Download</button>
  </div>
);

const statusColor = (s) => ({
  available: 'bg-yellow-400/15 text-yellow-300 border border-yellow-400/30',
  allocated: 'bg-amber-500/20 text-amber-300 border border-amber-400/30',
  active: 'bg-green-500/20 text-green-300 border border-green-400/30',
  activating: 'bg-orange-500/20 text-orange-300 border border-orange-400/30',
  deactivated: 'bg-neutral-700 text-neutral-300 border border-neutral-500/30',
})[s] || 'bg-neutral-800 text-neutral-300';
